/**
 * When an armed tablet may hold the kiosk screen with no CRM user behind it.
 *
 * THE RULE, IN ONE PLACE. Two gates stand in front of `/app/in-the-shop` —
 * `ProtectedRoute` (is anyone signed in?) and a `RoleGate` (may this role arm a
 * kiosk?) — and BOTH have to agree, or the tablet is bounced to `/login` the
 * moment the CRM session ends. Writing the condition twice is how they would
 * drift; it is named once here and imported by both.
 *
 * Why the exception is safe: the `ShopFloorSession` is a real credential with
 * its own 12-hour life, held by the tablet rather than by a person, and
 * `tablet.md` already requires floor mode to outlive JWT₁. The screen it admits
 * shows a PIN pad and the waiting banner; every floor ACTION behind it needs
 * the JWT₃ that only a correct PIN can mint. A role is required to ARM a
 * tablet — that check is untouched — but an already-armed tablet has been
 * vouched for and needs no role to keep showing its own lock screen.
 */

/** The one path this exception covers. */
export const KIOSK_PATH = "/app/in-the-shop";

/**
 * G-133 / OD-168 (owner, 2026-10-09): kiosk mode runs ONLY inside the tablet
 * app — the Capacitor shell (`isNativeShell()`). Every browser is refused:
 * phones, desktops, and the tablet's own Chrome alike.
 *
 * The local dev server is the one exception, so the kiosk can still be
 * checked on a developer's machine; a production build never takes it.
 *
 * A UI gate, not a credential: the server cannot tell the shell from a
 * browser, and every floor action behind the lock screen still needs a PIN.
 */
export function kioskAllowedOnThisDevice(
  isShell: boolean,
  isDevBuild: boolean,
): boolean {
  return isShell || isDevBuild;
}

/**
 * Pure so it can be tested: a tablet that holds a shop floor session id, with
 * nobody signed into the CRM, may stay on the kiosk screen.
 */
export function armedTabletMayEnter(
  isAuthenticated: boolean,
  shopFloorSessionId: string | null,
): boolean {
  return !isAuthenticated && shopFloorSessionId !== null;
}

/**
 * "I want this tablet out of kiosk mode, and I am about to prove who I am."
 *
 * Set when someone presses Exit, consumed after they come back signed in. It
 * exists so the round trip through the login page is ONE gesture for the
 * florist rather than two — press Exit, sign in, done.
 *
 * sessionStorage, beside the shop floor session id: per-tab, cleared with the
 * app, and never a credential — it only records an intention. Reading it
 * CONSUMES it, so a stale flag cannot disarm a tablet days later.
 */
const DISARM_INTENT_KEY = "fw-kiosk-disarm-intent";

export function setDisarmIntent(): void {
  try {
    sessionStorage.setItem(DISARM_INTENT_KEY, String(Date.now()));
  } catch {
    /* a tablet with no storage simply never auto-disarms */
  }
}

/** When Exit was pressed, or null. PEEKS — a stale intent is not consumed. */
export function disarmIntentAt(): number | null {
  try {
    const raw = sessionStorage.getItem(DISARM_INTENT_KEY);
    if (raw === null) return null;
    const at = Number(raw);
    return Number.isFinite(at) ? at : null;
  } catch {
    return null;
  }
}

export function clearDisarmIntent(): void {
  try {
    sessionStorage.removeItem(DISARM_INTENT_KEY);
  } catch {
    /* nothing to clear */
  }
}

/**
 * May the tablet disarm itself now?
 *
 * ONLY FOR A SIGN-IN THAT HAPPENED AFTER EXIT WAS PRESSED. Clearing the CRM
 * session in memory is not enough on its own: the refresh cookie is httpOnly
 * and survives, so the very next page load calls `restoreSession()` and brings
 * the same session back — with `loginAtMs` restored to its ORIGINAL value
 * (`apiClient.ts`, runRestoreSession: `persistedStart ?? Date.now()`).
 *
 * Measured on the tablet 2026-09-14: navigating to /app after pressing Exit
 * restored the manager's session and disarmed the tablet with nobody signing
 * in — which is the whole thing this was built to prevent. A restored session
 * carries a login time from BEFORE the request; only a real sign-in carries one
 * after it.
 */
export function mayAutoDisarm(
  requestedAt: number | null,
  loginAt: number | null,
): boolean {
  if (requestedAt === null || loginAt === null) return false;
  return loginAt > requestedAt;
}
